import MuiAutocomplete from '@mui/material/Autocomplete';
import type { AutocompleteProps, AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { forwardRef } from 'react';
import type { ReactElement, ReactNode, Ref } from 'react';
import { toVendor } from '../styles';
import type { Styleable } from '../styles';
import { SIZE } from '../wrap';
import type { ControlSize } from '../wrap';

/**
 * What `renderInput` receives, in this library's vocabulary.
 *
 * The vendor hands the render callback its own `size` spelling, and the callback
 * spreads the lot straight into our TextField — so without translating here, the
 * vendor's words reappear at every `renderInput` call site, which is exactly what
 * the seam exists to prevent.
 */
export type RenderInputParams =
  & Omit<AutocompleteRenderInputParams, 'size'>
  & { size?: ControlSize }
  /** The vendor moved these into `slotProps`; the render callbacks still say them. */
  & { InputProps: Record<string, unknown>; inputProps: Record<string, unknown> };

const OUR_SIZE: Record<string, ControlSize> = { small: 'sm', medium: 'md', large: 'lg' };

/**
 * Four type parameters, all of which the callers depend on: the option type, and
 * the three booleans that decide whether `value` is one option, several, or a
 * free-typed string. `wrap` would erase them.
 */

export type AutocompleteOwnProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> =
  & Omit<
      AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
      'sx' | 'size' | 'component' | 'renderInput' | 'renderValue'
    >
  & Styleable
  & {
      size?: ControlSize;
      renderInput: (params: RenderInputParams) => ReactNode;
      /** The vendor renamed this to `renderValue`; forwarded below. */
      renderTags?: (value: T[], getTagProps: (o: { index: number }) => object) => ReactNode;
    };

/** Built once without the type parameters, then given them back below. */
const AutocompleteBase = forwardRef(function Autocomplete(
  {
    size,
    styles,
    vendorStyles,
    renderInput,
    renderTags,
    ...rest
  }: AutocompleteOwnProps<unknown, boolean | undefined, boolean | undefined, boolean | undefined>,
  ref: Ref<unknown>,
) {
  return (
    <MuiAutocomplete
      ref={ref}
      {...({
        ...rest,
        ...(size ? { size: SIZE[size] } : {}),
        // Only a multiple picker has tags. In single-select the vendor hands
        // `renderValue` one option, not an array, and the cast below would
        // lie about it -- the caller would then map over a single object.
        ...(renderTags && rest.multiple
          ? {
              renderValue: (value: unknown, getItemProps: unknown) =>
                renderTags(
                  value as unknown[],
                  getItemProps as (o: { index: number }) => object,
                ),
            }
          : {}),
        renderInput: (params: AutocompleteRenderInputParams) => {
          const { size: vendorSize, ...restParams } = params;
          const bag = (params as { slotProps?: Record<string, unknown> }).slotProps ?? {};
          return renderInput({
            ...restParams,
            ...(vendorSize ? { size: OUR_SIZE[vendorSize] } : {}),
            InputProps: (bag.input ?? {}) as Record<string, unknown>,
            inputProps: (bag.htmlInput ?? {}) as Record<string, unknown>,
          });
        },
        sx: toVendor(styles, vendorStyles),
      } as unknown as AutocompleteProps<unknown, boolean | undefined, boolean | undefined, boolean | undefined>)}
    />
  );
});

export const Autocomplete = AutocompleteBase as unknown as <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: AutocompleteOwnProps<T, Multiple, DisableClearable, FreeSolo> & { ref?: Ref<unknown> },
) => ReactElement | null;
